import { PRESETS_INSPECTION_RESULT } from 'app/modules/inspection/constants';
import { QMASLazyDocument, Status } from 'app/modules/ui';
import { ShippingInformationDetail } from './Detail';
import { ShippingInformation } from 'app/definitions/interfaces/Product';

export const LABEL_WIDTH = '50%';

const convertToNegative = (value: number) => {
  return value * -1;
};

export const computeStatusAndRateInTolerance = (
  value,
  productData,
  tolerance: [number, number]
) => {
  const computedProgress =
    ((parseFloat(value) - parseFloat(productData)) / parseFloat(productData)) *
    100;
  const progress =
    computedProgress === Infinity || isNaN(computedProgress)
      ? 0.0
      : computedProgress;

  const [toleranceUpper, toleranceLower] = tolerance;

  const transformToHumanReadable = (value: number) => {
    return `${value > 0 ? '+' : ''}${parseFloat(`${value}`).toFixed(2)}%`;
  };

  if (
    progress >= (convertToNegative(toleranceLower) || 0) &&
    progress <= (toleranceUpper || 0)
  ) {
    return {
      status: 'passed',
      percentage: transformToHumanReadable(progress)
    };
  }
  if (
    progress < (convertToNegative(toleranceLower) || 0) ||
    progress > (toleranceUpper || 0)
  ) {
    return {
      status: 'failed',
      percentage: transformToHumanReadable(progress)
    };
  }
  return {
    status: 'pending',
    percentage: `${
      isNaN(progress) ? '-' : transformToHumanReadable(progress)
    }` as string
  };
};

export const DESCRIPTION_SHIPPING_INFORMATION = [
  {
    label: 'Result',
    dataIndex: 'result',
    span: 3,
    render: (value: string) => {
      if (value) {
        const [text, color] = PRESETS_INSPECTION_RESULT.get(value) || [];
        return <Status type="text" value={text} color={color as string} />;
      }
    }
  },
  {
    label: 'Actual Dimensions',
    dataIndex: 'result',
    span: 3,
    render: (value: string) => {
      if (value) {
        const [text, color] = PRESETS_INSPECTION_RESULT.get(value) || [];
        return <Status type="text" value={text} color={color as string} />;
      }
    }
  },
  {
    label: 'Total Quantity / Inner Carton',
    dataIndex: 'totalQuantity',
    span: 3,
    render: (value: string) => {
      return value || 'N/A';
    }
  },
  {
    label: 'Total Quantity / Outer Carton',
    dataIndex: 'outerQuantity',
    span: 3,
    render: (value: string) => {
      return value || 'N/A';
    }
  },
  {
    label: 'No. of Inner / Outer Carton',
    dataIndex: 'outerCarton',
    span: 3,
    render: (value: string) => {
      return value || 'N/A';
    }
  },
  {
    label: '',
    dataIndex: 'result',
    span: 3,
    render: (
      value: string,
      record,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      return (
        <ShippingInformationDetail
          value={record}
          shippingInformation={shippingInformation}
        />
      );
    }
  },

  {
    label: 'Remarks',
    dataIndex: 'remark',
    span: 3,
    render: (value: string) => {
      return value || 'N/A';
    }
  },
  {
    label: 'Reference Media',
    dataIndex: 'referenceMedia',
    className: 'reference-media',
    render: (value: any) => {
      if (Array.isArray(value) && value.length) {
        return (
          <div style={{ display: 'flex', gap: '10px', height: 65 }}>
            {value.map((item) => {
              return (
                <QMASLazyDocument
                  key={item}
                  alt="attachments"
                  documentId={item}
                  lazyProps={{
                    debounce: 500,
                    overflow: true
                  }}
                />
              );
            })}
          </div>
        );
      }
      return null;
    }
  }
];

export const DESCRIPTION_PRODUCT_PREFERENCE = [
  {
    label: 'Dim. Tolerance (Length)',
    dataIndex: 'tolerance',
    labelStyle: { width: '50%' },
    render: (
      _,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      const tolerance = [
        `+${shippingInformation?.lengthUpperTolerance ?? ''}% `,
        `-${shippingInformation?.lengthLowerTolerance ?? ''}%`
      ];
      const lengthAndUnit = [
        `${shippingInformation?.length ?? ''}${
          shippingInformation.lengthUnit ?? ''
        }`
      ];
      return [tolerance.join(' ; '), lengthAndUnit.join(' ')].join(' / ');
    }
  },
  {
    label: 'Dim. Tolerance (Width)',
    dataIndex: 'tolerance',
    labelStyle: { width: '50%' },
    render: (
      _,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      const tolerance = [
        `+${shippingInformation?.widthUpperTolerance ?? ''}% `,
        `-${shippingInformation?.widthLowerTolerance ?? ''}%`
      ];
      const lengthAndUnit = [
        `${shippingInformation?.width ?? ''}${
          shippingInformation.widthUnit ?? ''
        }`
      ];
      return [tolerance.join(' ; '), lengthAndUnit.join(' ')].join(' / ');
    }
  },
  {
    label: 'Dim. Tolerance (Height)',
    dataIndex: 'tolerance',
    labelStyle: { width: '50%' },
    render: (
      _,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      const tolerance = [
        `+${shippingInformation?.heightUpperTolerance ?? ''}% `,
        `-${shippingInformation?.heightLowerTolerance ?? ''}%`
      ];
      const valueAndUnit = [
        `${shippingInformation?.height ?? ''}${
          shippingInformation.heightUnit ?? ''
        }`
      ];
      return [tolerance.join(' ; '), valueAndUnit.join(' ')].join(' / ');
    }
  },
  {
    label: 'Net Weight Tolerance',
    dataIndex: 'tolerance',
    labelStyle: { width: '50%' },
    render: (
      _,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      const tolerance = [
        `+${shippingInformation?.netWeightUpperTolerance ?? ''}% `,
        `-${shippingInformation?.netWeightLowerTolerance ?? ''}%`
      ];
      const valueAndUnit = [
        `${shippingInformation?.netWeight ?? ''}${
          shippingInformation.netWeightUnit ?? ''
        }`
      ];
      return [tolerance.join(' ; '), valueAndUnit.join(' ')].join(' / ');
    }
  },
  {
    label: 'Gross Weight Tolerance',
    dataIndex: 'tolerance',
    labelStyle: { width: '50%' },
    render: (
      _,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      const tolerance = [
        `+${shippingInformation?.grossWeightUpperTolerance ?? ''}% `,
        `-${shippingInformation?.grossWeightLowerTolerance ?? ''}%`
      ];
      const valueAndUnit = [
        `${shippingInformation?.grossWeight ?? ''}${
          shippingInformation.grossWeightUnit ?? ''
        }`
      ];
      return [tolerance.join(' ; '), valueAndUnit.join(' ')].join(' / ');
    }
  }
];

export const DESCRIPTION_ACTUAL_PRODUCT_INFO = [
  {
    label: 'Length',
    dataIndex: 'length',
    labelStyle: { width: '50%' },
    render: (
      value,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { status, percentage } = computeStatusAndRateInTolerance(
        value,
        parseFloat(meta.shippingInformation.length || '0'),
        [
          parseFloat(meta.shippingInformation.lengthUpperTolerance || '0'),
          parseFloat(meta.shippingInformation.lengthLowerTolerance || '0')
        ]
      );
      const [, color] = PRESETS_INSPECTION_RESULT.get(status) || [];
      return (
        <span>
          <Status value={percentage} color={color as string} type="text" />
          <span>/ </span>
          <span>{`${value || 0}`}</span>
          <span>{meta.shippingInformation.lengthUnit ?? ''}</span>
        </span>
      );
    }
  },
  {
    label: 'Width',
    dataIndex: 'width',
    labelStyle: { width: '50%' },
    render: (
      value,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { status, percentage } = computeStatusAndRateInTolerance(
        value,
        parseFloat(meta.shippingInformation.width || '0'),
        [
          parseFloat(meta.shippingInformation.widthUpperTolerance || '0'),
          parseFloat(meta.shippingInformation.widthLowerTolerance || '0')
        ]
      );
      const [, color] = PRESETS_INSPECTION_RESULT.get(status) || [];
      return (
        <span>
          <Status value={percentage} color={color as string} type="text" />
          <span>/ </span>
          <span>{`${value || 0}`}</span>
          <span>{meta.shippingInformation.widthUnit ?? ''}</span>
        </span>
      );
    }
  },
  {
    label: 'Height',
    dataIndex: 'height',
    labelStyle: { width: '50%' },
    render: (
      value,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { status, percentage } = computeStatusAndRateInTolerance(
        value,
        parseFloat(meta.shippingInformation.height || '0'),
        [
          parseFloat(meta.shippingInformation.heightUpperTolerance || '0'),
          parseFloat(meta.shippingInformation.heightLowerTolerance || '0')
        ]
      );
      const [, color] = PRESETS_INSPECTION_RESULT.get(status) || [];
      return (
        <span>
          <Status value={percentage} color={color as string} type="text" />
          <span>/ </span>
          <span>{`${value || 0}`}</span>
          <span>{meta.shippingInformation.heightUnit ?? ''}</span>
        </span>
      );
    }
  },
  {
    label: 'Net Weight',
    dataIndex: 'netWeight',
    labelStyle: { width: '50%' },
    render: (
      value,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { status, percentage } = computeStatusAndRateInTolerance(
        value,
        parseFloat(meta.shippingInformation.netWeight || '0'),
        [
          parseFloat(meta.shippingInformation.netWeightUpperTolerance || '0'),
          parseFloat(meta.shippingInformation.netWeightLowerTolerance || '0')
        ]
      );
      const [, color] = PRESETS_INSPECTION_RESULT.get(status) || [];
      return (
        <span>
          <Status value={percentage} color={color as string} type="text" />
          <span>/ </span>
          <span>{`${value || 0}`}</span>
          <span>{meta.shippingInformation.netWeightUnit ?? ''}</span>
        </span>
      );
    }
  },
  {
    label: 'Gross Weight',
    dataIndex: 'grossWeight',
    labelStyle: { width: '50%' },
    render: (
      value,
      __,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { status, percentage } = computeStatusAndRateInTolerance(
        value,
        parseFloat(meta.shippingInformation.grossWeight || '0'),
        [
          parseFloat(meta.shippingInformation.grossWeightUpperTolerance || '0'),
          parseFloat(meta.shippingInformation.grossWeightLowerTolerance || '0')
        ]
      );
      const [, color] = PRESETS_INSPECTION_RESULT.get(status) || [];
      return (
        <span>
          <Status value={percentage} color={color as string} type="text" />
          <span>/ </span>
          <span>{`${value || 0}`}</span>
          <span>{meta.shippingInformation.grossWeightUnit ?? ''}</span>
        </span>
      );
    }
  }
];

export const DESCRIPTION_ADDITIONAL_INFO = [
  {
    label: 'Barcode',
    dataIndex: 'actualBarcode',
    labelStyle: { width: '50%' },
    render: (
      value,
      _,
      meta: {
        shippingInformation: ShippingInformation;
      }
    ) => {
      const { shippingInformation } = meta;
      return (
        <span>
          <Status
            type="text"
            value={value}
            color={value !== shippingInformation.barcode ? 'orange' : 'default'}
          />
          /<span>{shippingInformation.barcode}</span>
        </span>
      );
    }
  }
];
